// @flow

import _ from 'lodash';

import ArrayUtils from '../../utils/ArrayUtils';
import Utils from '../../utils/Utils';
import StringUtils from '../../utils/StringUtils';

import ApiObject from '../ApiObject';
import OrderMenuPart from './OrderMenuPart';
import OrderProduct from './OrderProduct';
import RestaurantMenu from '../menu/RestaurantMenu';
import Menu from '../menu/Menu';
import MenuPart from '../menu/MenuPart';
import MenuPartProduct from '../menu/MenuPartProduct';
import CategoryItem from '../menu/CategoryItem';

export default class OrderMenu extends ApiObject {
  id: string;

  name: string;

  parts: Array<OrderMenuPart> = [];

  comment: string;

  price: number = 0;

  basePrice: number = 0;

  orderId: string;

  /**
    * Sets the quantity of the same product. Front only
    * Before sending the order, we split the OrderProduct with the quantity
    */
  quantity: number = 1;

  // used to store ui data, will be merged back with order-preview
  ui: Object = {};

  postJsonAssignation(data: Object) {
    this.parts = [];
    _.each(data.parts, (partData) => {
      const orderMenuPart:OrderMenuPart = new OrderMenuPart();
      orderMenuPart.fromJson(partData);
      this.parts.push(orderMenuPart);
    });
  }

  fromMenu(menu: Menu, mode: OrderMode) {
    this.id = menu.id;
    this.name = menu.name;
    this.basePrice = menu.getPriceForMode(mode);
    this.price = this.basePrice;

    if (!_.isEmpty(menu.cartModifiers)) {
      this.price = menu.getCartModifiersPrice(mode);
    }
    return this;
  }

  equals(other: OrderMenu): boolean {
    return this.id === other.id && this.comment === other.comment && this.partsEquals(other.parts) && this.price === other.price;
  }

  partsEquals(otherParts: Array<OrderMenuPart>): boolean {
    if (otherParts.length !== this.parts.length) {
      return false;
    }

    for (let i = 0; i < this.parts.length; i++) {
      if (!this.parts[i].equals(otherParts[i])) {
        return false;
      }
    }

    return true;
  }

  getAllProducts(): Array<OrderProduct> {
    let allProducts = [];

    _.each(this.parts, (part: OrderMenuPart) => {
      allProducts = _.concat(allProducts, part.products);
    });

    return allProducts;
  }

  /**
   * Merge options of `other` Order Product on this
   */
  mergeWithOrderMenu(menuInfo: Menu, other: OrderMenu) {
    if (_.isNil(other)) {
      return ;
    }

    const otherAllOrderProducts: Array<OrderProduct> = other.getAllProducts();

    _.each(menuInfo.parts, (part: MenuPart) => {
      _.each(part.products, (menuPartProduct: MenuPartProduct) => {
        const productOnOther = ArrayUtils.find(otherAllOrderProducts, (orderProduct: OrderProduct) => {
          return orderProduct.id === menuPartProduct.id;
        });

        if (!_.isNull(productOnOther)) {
          const orderProductToPart = productOnOther.copy();
          orderProductToPart.baseAdditionalPrice = menuPartProduct.additionalPrice;
          orderProductToPart.additionalPrice = menuPartProduct.additionalPrice;

          // the product is on the menu and have been set on the other order menu.
          // we add it to hour part.
          this.addProductToPart(orderProductToPart, part);
        }
      });
    });
  }

  /**
   * Merge options of `other` Order Product on this
   */
  mergeWithOrderProduct(menuInfo: Menu, orderProductToMerge: OrderProduct) {
    if (_.isNil(orderProductToMerge)) {
      return ;
    }


    // find product on menu
    _.each(menuInfo.parts, (part: MenuPart) => {
      _.each(part.products, (menuPartProduct: MenuPartProduct) => {
        if (menuPartProduct.id === orderProductToMerge.id) {
          const orderProductToPart = orderProductToMerge.copy();
          orderProductToPart.baseAdditionalPrice = menuPartProduct.additionalPrice;
          orderProductToPart.additionalPrice = menuPartProduct.additionalPrice;

          // the product to merge can be set on this part
          this.addProductToPart(orderProductToPart, part);
        }
      });
    });
  }

  //
  //
  //

  getDescription(): string {
    let description = '';
    const separator = ', ';

    _.each(this.parts, (part: OrderMenuPart) => {
      description += part.getDescription() + separator;
    });

    // remove last separator.
    return description.substring(0, description.length - separator.length);
  }

  getProductsComment(): string {
    let comment = '';
    const separator = ', ';

    _.each(this.parts, (part: OrderMenuPart) => {
      const productsComment = part.getProductsComment();
      if (!_.isEmpty(productsComment)) {
        comment += productsComment + separator;
      }
    });

    // remove last separator.
    return StringUtils.removeAtEnd(comment, separator);
  }

  getDescriptionWithComments(): string {
    let description = '';
    const separator = ', ';

    _.each(this.parts, (part: OrderMenuPart) => {
      description += part.getDescriptionWithProductComments() + separator;
    });

    // remove last separator.
    return description.substring(0, description.length - separator.length);
  }

  getProductsWithCommentArray() {
    let products = [];

    _.each(this.parts, (part: OrderMenuPart) => {
      products = _.concat(products, part.getProductsWithCommentArray());
    });

    return products;
  }

  //
  //
  //

  getPartWithId(partId: string): ?OrderMenuPart {
    return ArrayUtils.find(this.parts, { id: partId });
  }

  //
  //
  //

  isValidWithRestaurantMenu(restaurantMenu: RestaurantMenu): boolean {
    let res = true;

    if (_.isEmpty(this.parts)) {
      return false;
    }

    const menu: Menu = restaurantMenu.getMenuWithId(this.id);

    // verify all parts are here
    _.each(menu.parts, (menuPart: MenuPart) => {
      const found = this.getPartWithId(menuPart.id);

      // if not found and at least on product is required.
      if (!found && menuPart.min !== 0) {
        res = false;
        return false; // quit loop.
      }
    });

    if (!res) {
      return false;
    }

    // verify all parts are valid
    _.each(this.parts, (part: OrderMenuPart) => {
      if (!part.isValidWithRestaurantMenu(restaurantMenu, this.id)) {
        res = false;
        return false; // quit loop.
      }
    });

    return res;
  }

  isPartWithIdValidWithRestaurantMenu(partId: string, restaurantMenu: RestaurantMenu) {
    const part = this.getPartWithId(partId);

    if (_.isNil(part)) {
      return false;
    }

    return part.isValidWithRestaurantMenu(restaurantMenu, this.id);
  }

  //
  // Prices
  //

  getTotalPrice(): number {
    return this.price + this.getTotalPartsAdditionnalPrice();
  }

  getTotalPriceWithQuantity(): number {
    return this.getTotalPrice() * this.quantity;
  }

  getTotalPartsAdditionnalPrice(): number {
    let total = 0;
    _.each(this.parts, (part: OrderMenuPart) => {
      total += part.getTotalAdditionnalPrice();
    });
    return total;
  }

  //
  //
  //

  populateOrderId() {
    this.orderId = `m${Utils.generateUID()}`;
  }

  //
  //
  //
  addProductToPart(orderProduct: OrderProduct, menuPart: MenuPart) {
    let orderMenuPart: OrderMenuPart = this.getPartWithId(menuPart.id);

    if (_.isNull(orderMenuPart)) {
      orderMenuPart = new OrderMenuPart().fromMenuPart(menuPart);
      this.parts.push(orderMenuPart);
    }

    orderMenuPart.addProduct(orderProduct);
  }

  updateProductOnPart(orderProduct: OrderProduct, menuPart: MenuPart) {
    let orderMenuPart: OrderMenuPart = this.getPartWithId(menuPart.id);

    if (_.isNull(orderMenuPart)) {
      orderMenuPart = new OrderMenuPart().fromMenuPart(menuPart);
      this.parts.push(orderMenuPart);
    }

    orderMenuPart.updateProduct(orderProduct);
  }

  removeProductWithIdFromPart(orderProductId: string, menuPart: MenuPart) {
    let orderMenuPart: OrderMenuPart = this.getPartWithId(menuPart.id);

    if (_.isNull(orderMenuPart)) {
      orderMenuPart = new OrderMenuPart().fromMenuPart(menuPart);
      this.parts.push(orderMenuPart);
    }

    orderMenuPart.removeProductWithId(orderProductId);
  }

  haveProductWithIdForPartWithId(productId: string, partId: string) {
    let orderMenuPart: OrderMenuPart = this.getPartWithId(partId);

    if (_.isNull(orderMenuPart)) {
      return false;
    }

    return orderMenuPart.haveProductWithId(productId);
  }
}



// WEBPACK FOOTER //
// ./sdk/scripts/entities/order/OrderMenu.js